import React from 'react'

import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { Avatar } from '@repo/ui'

/**
 * A row of overlapping user avatars, presentational only.
 *
 * ## Why this exists when `UserGroupCell` already draws overlapping avatars
 *
 * `UserGroupCell` (components/display/Table/.../Cells.tsx) is a TABLE CELL, and three
 * things in it are load-bearing for that job and disqualifying outside it:
 *
 *   1. **`route` is required, and every avatar is a `RouterLink`.** Inside a card that is
 *      itself `role="link"` that is a link nested in a link — invalid ARIA, and a
 *      click-target trap where aiming at a face navigates somewhere the user did not mean
 *      to go. The customer support card documents the same trap for the same reason.
 *   2. **Cell geometry is hardcoded** — `p: 2, pl: 0, width: '100%'`, avatars at 48px. In
 *      a card's meta line that is a block, not a line.
 *   3. **One user short-circuits to `UserCell`**, which renders a two-line avatar + name +
 *      email block. A meta line wants one line.
 *
 * So this is the presentational core with none of the cell's assumptions: no navigation,
 * no padding, no width, and it renders 1..N the same way. `UserGroupCell` should
 * eventually be rebuilt on top of it — that is the §36 convergence — but rewriting it now
 * would change how Roles, Companies and Leads render for a reason unrelated to any of them.
 *
 * ## Identity
 *
 * Uses `@repo/ui`'s `Avatar`, which owns the image → initials → glyph fallback and derives
 * initials from a name OR an email (`"spencer@vera.inc"` → "S"). That matters here: a
 * support request often has an email and no name, and the platform-local initials helpers
 * split on spaces only, so they return "" for exactly that case. It also avoids adding a
 * fourth initials implementation to the three already in the tree.
 */

export interface AvatarGroupUser {
  id?: string | null
  name?: string | null
  email?: string | null
  /** Profile image, when there is one. Falls back to initials. */
  avatar?: string | null
}

interface Props {
  users: AvatarGroupUser[]
  /** Avatar diameter. Default 24 — a meta-line size, not a table-row one. */
  size?: number
  /** How many faces to draw before collapsing the rest into "+N". Default 4. */
  max?: number
}

/**
 * What to call someone when the name is missing: the EMAIL, never a placeholder
 * (VERA-304). The `|| 'Unknown'` tail was only ever reachable for a user with
 * neither a name nor an address, and it stood in for the far more common case —
 * the server used to send `name: 'Unknown'`, which is truthy, so this fell at the
 * first branch and the tooltip read "Unknown" for anyone without a name.
 * Fixed at the source; an empty string here now correctly falls through to the
 * address. A person with neither gets an empty tooltip, which is honest.
 */
const label = (user: AvatarGroupUser): string =>
  user.name?.trim() || user.email?.trim() || ''

export const UserAvatarGroup = ({ users, size = 24, max = 4 }: Props) => {
  const theme = useTheme()

  if (users.length === 0) return null

  const shown = users.slice(0, max)
  const surplus = users.length - shown.length

  /*
    A ring in the PAGE background colour, so overlapping circles read as separate faces
    rather than one merged blob. `background.paper` would be wrong: these sit on a Card,
    whose surface is `background.paper`, and a ring the same colour as what is behind it
    is invisible — which is the whole point of drawing one.
  */
  const ring = `2px solid ${theme.palette.background.paper}`

  return (
    <Stack direction="row" alignItems="center">
      {shown.map((user, index) => (
        <Tooltip
          // Name over email on two lines when both are known; whichever exists otherwise.
          // `pre-line` so the \n is honoured rather than collapsed to a space.
          title={
            <span style={{ whiteSpace: 'pre-line' }}>
              {user.name?.trim() && user.email?.trim()
                ? `${user.name.trim()}\n${user.email.trim()}`
                : label(user)}
            </span>
          }
          arrow
          key={user.id ?? user.email ?? index}
        >
          {/* The Box, not the Avatar, carries the overlap and the ring — `Avatar` owns its
              own sizing, and reaching into it to add a border would fight that. The last
              avatar drawn sits on top by document order, so `zIndex` descends to put the
              FIRST face in front, matching how every avatar stack in the app reads. */}
          <Box
            sx={{
              ml: index === 0 ? 0 : `-${Math.round(size / 3)}px`,
              zIndex: shown.length - index,
              borderRadius: '50%',
              border: ring,
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <Avatar src={user.avatar} name={label(user)} size={size} />
          </Box>
        </Tooltip>
      ))}

      {surplus > 0 && (
        // Everyone the row did not draw, named in the tooltip. A searchable roster popover
        // (what the table cell opens) would be a second click target inside a card that is
        // already one link — see the note at the top.
        <Tooltip
          title={
            <span style={{ whiteSpace: 'pre-line' }}>
              {users.slice(max).map(label).join('\n')}
            </span>
          }
          arrow
        >
          <Box
            sx={{
              ml: `-${Math.round(size / 3)}px`,
              width: size,
              height: size,
              borderRadius: '50%',
              border: ring,
              backgroundColor: theme.palette.primary[100],
              color: theme.palette.text.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: Math.round(size / 2.6), fontWeight: 600, lineHeight: 1 }}>
              +{surplus}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Stack>
  )
}
